import { aliasOf, property, subclass } from '@arcgis/core/core/accessorSupport/decorators';
import { messageBundle, tsx } from '@arcgis/core/widgets/support/widget';
import Widget from '@arcgis/core/widgets/Widget';

import NavBarViewModel, { navbarRoute, sortFields, sortPair } from './NavBar/NavBarViewModel';
import NavBarT9N from "../../t9n/ui/NavBar/NavBar.json";
import HeaderT9N from "../../t9n/ui/Header/Header.json";
import { VNode } from 'src/interfaces/interfaces';

import "./NavBar/styles/NavBar.scss";
import SearchBox from '../widgets/SearchBox';
import { init, whenDefinedOnce } from '@arcgis/core/core/watchUtils';

import Logo from "../../../public/assets/images/arcgis-instant-apps-96.png";
import { applyShadowStyle } from '../utils/utils';
import { SHADOW_STYLE_NAVBAR } from '../utils/shadowStyles';

export interface NavBarProperties extends __esri.WidgetProperties {
  name?: string;
  view?: __esri.MapView | __esri.SceneView;
}

const MOBILE_VIEW_BREAK_POINT: number = 945; // (Needs to match hardcoded CSS val in Navbar.scss)
const PRODUCT_NAME_STR: string = "Instant Apps";

@subclass('Landing.ui.NavBar')
export default class NavBar extends Widget {

  @property()
  @messageBundle("dist/assets/t9n/ui/NavBar/NavBar")
  messages: typeof NavBarT9N;

  @property()
  @messageBundle("dist/assets/t9n/ui/Header/Header")
  headerMessages: typeof HeaderT9N;

  @property()
  @aliasOf("viewModel.appMessages")
  @messageBundle("dist/assets/t9n/App/App")
  appMessages;

  @property()
  TabStates;

  // @property()
  // selectedTab: string;

  @property()
  searchBox: SearchBox = new SearchBox();

  @property()
  @aliasOf("viewModel.portal")
  portal: __esri.Portal;

  @property({
    type: NavBarViewModel
  })
  viewModel: NavBarViewModel = new NavBarViewModel();

  @property()
  _sortTermsToSortFields: [string, sortFields][];
  @property()
  _isMobileView: boolean = false;

  constructor(properties?: NavBarProperties) {
    super(properties);
    window.addEventListener('resize', this._handleWindowResize.bind(this));
  }

  postInitialize() {
    this._handleWindowResize();
    whenDefinedOnce(this, "searchBox", () => {
      this.viewModel.searchBox = this.searchBox;
    });

    this.searchBox.navbarVM = this.viewModel;

    if (!this.portal.isPortal) {
      // Online Tabs
      this.TabStates = {
        "browseAll": this.messages.BrowseAll,
        "featured": this.messages.Featured,
        "myApps": this.messages.MyApps
      };
    } else {
      // Enterprise Tabs
      this.TabStates = {
        "browseAll": this.messages.BrowseAll,
        "myApps": this.messages.MyApps
      };
    }

    // Default Tab
    this._performChangeTab(this.viewModel.navigationRoute ?? "browseAll");

    init(this.viewModel, "navigationRoute", (route: navbarRoute) => {
      // Update Sort for new Tab
      const sortFieldsForNewTab = this._decideSortFieldsForTab();
      this.viewModel.setCurrentSort(sortFieldsForNewTab);
      this._sortTermsToSortFields = sortFieldsForNewTab;

      // update Search for new Tab
      this.viewModel.setCurrentSearchFilter();
      this.scheduleRender();
    });

    this.scheduleRender();
  }

  render() {
    const headerTitle = this._renderHeader();
    const search = this._renderSearch();
    const nav = this._renderNav();
    const btns = this._renderButtons();

    return [
      (<div class="navbar">
        {headerTitle}
        {search}
        {nav}
        {btns}
      </div>),
      this.viewModel?.onboardingModal?.render(),
      this._shouldRenderFilterChips()
    ];
  }

  private _decideSortFieldsForTab(): sortPair[] {
    const { sortingTerms } = this.appMessages;
    switch (this.viewModel.navigationRoute) {
      case "browseAll":
        return this.viewModel.decideBrowseAllAndFeatSorts();
      case "featured":
        return this.viewModel.decideBrowseAllAndFeatSorts();
      case "myApps":
        return [
          [sortingTerms.date, "modified"],
          [sortingTerms.name, "title"]
        ];
    }
  }

  private _renderHeader(): VNode {
    return (
      <div class="app-layout__header">
        <h2 class="header">
          <img src={Logo} alt="" class="header-logo" /> {PRODUCT_NAME_STR}
        </h2>
      </div>
    )
  }

  private _renderSearch(): VNode {
    return (
      <div class="search">
        { this?.searchBox?.render()}
      </div>
    )
  }

  private _renderNav(): VNode {
    return (
      <calcite-tabs layout="inline"
        position="above"
        class="links"
        afterCreate={(tabs) => applyShadowStyle(SHADOW_STYLE_NAVBAR, tabs)}>
        <calcite-tab-nav slot="tab-nav" class="links__tabs">
          {
            Object.keys(this?.TabStates)?.map((enumKey, index) => {
              return (
                <calcite-tab-title class="nav-option"
                  key={`navOption${index}`}
                  active={this.TabStates[this.viewModel.navigationRoute] === this.TabStates[enumKey] ? "true" : "false"}
                  onclick={this._performChangeTab.bind(this, enumKey)}
                  onkeydown={this._handleKeyboardChangeTab.bind(this, enumKey)}
                >{this.TabStates[enumKey]}</calcite-tab-title>
              );
            })
          }
        </calcite-tab-nav>
      </calcite-tabs>
    )
  }

  private _renderButtons(): VNode {
    return (
      <div class="buttons">
        {!this.portal.isPortal ? this._renderWelcomeButton() : null}
        {this._renderSort()}
        {this._renderSuggestionsButton()}
      </div>
    )
  }

  private _renderWelcomeButton(): VNode {
    const { onboardingStrings } = this.appMessages;
    return (
      <calcite-button
        class="welcome-btn"
        onclick={() => this.viewModel.openOnboarding()}
        color="neutral"
        scale="m"
        title={onboardingStrings?.launchBtnText}
        appearance="transparent"
        icon-start="mega-phone">
        {this.onlyShowTextWhenNotMobile(onboardingStrings?.launchBtnText)}
      </calcite-button>
    )
  }

  private _shouldRenderFilterChips(): VNode {
    return this.viewModel.navigationRoute != "myApps" && this?.viewModel?.filterChips?.chips?.length > 0
      ? this.viewModel?.filterChips?.render()
      : null;
  }

  private _renderSort(): VNode {
    const { sortingTerms } = this.appMessages;
    const { sortBy } = sortingTerms;
    return (
      <calcite-dropdown
        class="sort-dropdown"
        scale="l"
        width="m"
        type="click"
        placement="bottom-end"
      >
        <calcite-button
          color="neutral"
          appearance="transparent"
          icon-start="sort-descending"
          slot="dropdown-trigger"
          alignment="center"
          scale="m"
          width="auto"
          title={sortBy}
        >
          {this.onlyShowTextWhenNotMobile(this.viewModel.selectedSortField)}
        </calcite-button>

        <calcite-dropdown-group
          selection-mode="single"
          group-title={sortBy}
          role="menu"
          scale="l"
          title={sortBy}
          afterCreate={this._handleDropdownSelection.bind(this)}
        >
          {
            this._renderDropdownItems()
          }
        </calcite-dropdown-group>
      </calcite-dropdown>
    )
  }

  private _renderSuggestionsButton(): VNode {
    const { suggestions } = this.appMessages;
    return (
      <calcite-button
        class="sugg-btn"
        onclick={() => this.viewModel.toggleSuggBtnSignal()}
        color={this.viewModel.suggestionsButtonSignal ? "blue" : "neutral"}
        scale="m"
        title={suggestions?.suggestions}
        appearance={this.viewModel.suggestionsButtonSignal ? "outline" : "transparent"}
        icon-start="thumbs-up">
        {this.onlyShowTextWhenNotMobile(suggestions?.suggestions)}
      </calcite-button>
    )
  }

  private onlyShowTextWhenNotMobile(text: string): string | null {
    return !this._isMobileView ? text : null;
  }

  private _handleDropdownSelection(group: HTMLCalciteDropdownGroupElement): void {
    group.addEventListener("calciteDropdownItemChange", (e: any) => {
      this.viewModel.handleSortSelectionChange(e)
    });
  }

  private _renderDropdownItems(): VNode[] {
    return this._sortTermsToSortFields?.map((sortInfo: [string, sortFields], index: number) => {
      return (
        <calcite-dropdown-item
          key={`sortItem${index}`}
          role="menuitemradio"
          scale="l"
          selection-mode="single"
          tabindex="0"
          value={sortInfo[1]}
          active={sortInfo[1] === this.viewModel.sortField}
        >
          {sortInfo[0]}
        </calcite-dropdown-item>
      );
    })
  }

  private _handleKeyboardChangeTab(enumKey: navbarRoute, keyEvent: KeyboardEvent) {
    if (keyEvent.code === "Enter" || keyEvent.code === "Space") {
      this._performChangeTab(enumKey);
    }
  }

  private _performChangeTab(enumKey: navbarRoute) {
    if (enumKey != null) {
      this.viewModel.setNavbarRoute(enumKey);
      this.scheduleRender();
    }
  }

  private _handleWindowResize() {
    if (window.innerWidth <= MOBILE_VIEW_BREAK_POINT) {
      this._isMobileView = true;
      this.scheduleRender();
    } else {
      this._isMobileView = false;
      this.scheduleRender();
    }
  }

}
